// V25.9 — Mapping repair after a QuickBooks company-file switch.
//
// QBO account ids are sequential per company file and get reused, so a
// reconnect onto a new file leaves every saved id resolvable but pointing at
// a different account — liabilities in expenses, expenses merged into income.
// Mapping History can't fix it (every archived version is old-file ids too).
// This panel detects the scramble and re-points the mapping by account NAME.

import React, { useState, useEffect, useCallback } from 'react';
import { AlertTriangle, Wrench, Loader2 } from 'lucide-react';
import { mappingsAPI } from '../../lib/api';

const S = {
  panel: { margin: '0 0 20px', padding: '16px 18px', background: '#FEF2F2',
           border: '1px solid #F87171', borderRadius: 10 },
  h: { fontSize: 14, fontWeight: 700, color: '#7F1D1D', marginBottom: 6 },
  p: { fontSize: 12.5, color: '#991B1B', lineHeight: 1.65 },
  btn: { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px',
         borderRadius: 7, border: 'none', background: '#B91C1C', color: '#fff',
         fontSize: 12, fontWeight: 700, cursor: 'pointer' },
  ghost: { padding: '8px 14px', borderRadius: 7, border: '1px solid #CBD5E1',
           background: '#fff', color: '#334155', fontSize: 12, fontWeight: 600,
           cursor: 'pointer' },
  card: { marginTop: 12, padding: 12, background: '#fff', borderRadius: 8,
          border: '1px solid #FCA5A5' },
  row: { display: 'flex', gap: 8, alignItems: 'flex-start', padding: '7px 8px',
         borderRadius: 6, fontSize: 12, cursor: 'pointer' },
  mono: { fontFamily: 'JetBrains Mono, monospace', fontSize: 11 },
};

export default function MappingRepairPanel({ clientId, onApplied }) {
  const [health, setHealth] = useState(null);
  const [candidates, setCandidates] = useState(null);
  const [source, setSource] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState('');
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!clientId || clientId === 'demo') return;
    let dead = false;
    mappingsAPI.health(clientId)
      .then((r) => { if (!dead) setHealth(r.data); })
      .catch(() => {});
    return () => { dead = true; };
  }, [clientId]);

  const loadCandidates = useCallback(async () => {
    setBusy('candidates'); setError(null);
    try {
      const r = await mappingsAPI.remapCandidates(clientId);
      setCandidates(r.data);
      setSource(r.data.recommended_source);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally { setBusy(''); }
  }, [clientId]);

  const runPreview = async () => {
    setBusy('preview'); setError(null); setPreview(null);
    try {
      const r = await mappingsAPI.remapByName(clientId, source, true);
      setPreview(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally { setBusy(''); }
  };

  const apply = async () => {
    setBusy('apply'); setError(null);
    try {
      await mappingsAPI.remapByName(clientId, source, false);
      if (onApplied) onApplied();
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally { setBusy(''); }
  };

  if (!health || !(health.scrambled || health.realm_mismatch)) {
    // Healthy chart: keep the repair reachable (e.g. after a partial manual
    // cleanup pushed the score back above the alarm threshold) without
    // shouting about it.
    if (!health) return null;
    return (
      <div style={{ marginBottom: 16 }} data-testid="mapping-repair-quiet">
        {!candidates ? (
          <button style={{ ...S.ghost, fontSize: 11.5 }} onClick={loadCandidates}
                  disabled={busy === 'candidates'} data-testid="start-mapping-repair-btn">
            {busy === 'candidates' ? 'Loading…' : 'Re-point mapping to this QuickBooks file by account name'}
          </button>
        ) : (
          <div style={{ ...S.card, borderColor: '#CBD5E1' }} data-testid="mapping-repair-candidates">
            <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 8 }}>
              Which saved layout should I rebuild from?
            </div>
            {candidates.candidates.map((c) => (
              <label key={c.source} data-testid={`repair-source-${c.source}`}
                     style={{ ...S.row, background: source === c.source ? '#F1F5F9' : 'transparent' }}>
                <input type="radio" name="repair-source-quiet" checked={source === c.source}
                       onChange={() => { setSource(c.source); setPreview(null); }} />
                <span>
                  <strong>{c.label}</strong>
                  {c.source === candidates.recommended_source && ' — best match'}
                  <br />
                  <span style={S.mono}>
                    {c.matched} accounts recoverable · {c.dropped} not found ·{' '}
                    {c.section_consistency}% land in the right section
                  </span>
                </span>
              </label>
            ))}
            <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
              <button style={S.ghost} onClick={runPreview} disabled={!source || busy === 'preview'}
                      data-testid="preview-mapping-repair-btn">
                {busy === 'preview' ? 'Building preview…' : 'Preview changes'}
              </button>
              {preview && (
                <button style={S.btn} onClick={apply} disabled={busy === 'apply'}
                        data-testid="apply-mapping-repair-btn">
                  {busy === 'apply' ? 'Applying…' : 'Apply repair'}
                </button>
              )}
              <button style={S.ghost} onClick={() => { setCandidates(null); setPreview(null); }}>
                Cancel
              </button>
            </div>
            {preview && (
              <div style={{ ...S.mono, marginTop: 10 }} data-testid="mapping-repair-preview">
                {preview.repointed_count} re-pointed · {preview.already_correct_count} already
                correct · {preview.dropped_count} dropped · {preview.new_unmapped_count} new
                accounts left for review
              </div>
            )}
            {error && <div style={{ ...S.mono, marginTop: 8, color: '#B91C1C' }}
                           data-testid="mapping-repair-error">{error}</div>}
          </div>
        )}
      </div>
    );
  }

  return (
    <div style={S.panel} data-testid="mapping-repair-panel">
      <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
        <AlertTriangle size={18} color="#B91C1C" style={{ flexShrink: 0, marginTop: 2 }} />
        <div style={{ flex: 1 }}>
          <div style={S.h}>This mapping was built against a different QuickBooks company file</div>
          <div style={S.p}>
            QuickBooks assigns account IDs per company file and reuses them, so every saved
            line now points at a different account. Only{' '}
            <strong data-testid="mapping-health-consistency">{health.section_consistency}%</strong>{' '}
            of the {health.accounts_scored} mapped accounts still sit in a section that matches
            their QuickBooks classification — {health.misplaced_count} are in the wrong place.
            <br />
            <strong>Mapping History cannot fix this</strong> — every archived version is a list of
            old-file IDs too. Re-pointing by account name is the repair.
          </div>

          {health.misplaced?.length > 0 && (
            <div style={S.card} data-testid="mapping-health-misplaced">
              <div style={{ fontSize: 11, fontWeight: 700, color: '#7F1D1D', marginBottom: 6,
                            textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                Examples of what's misplaced
              </div>
              {health.misplaced.slice(0, 8).map((m) => (
                <div key={m.account_id} style={{ ...S.mono, color: '#7F1D1D', lineHeight: 1.8 }}>
                  {m.name} <span style={{ opacity: 0.7 }}>({m.classification || 'unknown'})</span>
                  {' → sitting in '}<strong>{m.section}</strong>
                </div>
              ))}
              {health.misplaced_count > 8 && (
                <div style={{ ...S.mono, opacity: 0.7, marginTop: 4 }}>
                  + {health.misplaced_count - 8} more
                </div>
              )}
            </div>
          )}

          {!candidates && (
            <button style={{ ...S.btn, marginTop: 14 }} onClick={loadCandidates}
                    disabled={busy === 'candidates'} data-testid="start-mapping-repair-btn">
              {busy === 'candidates'
                ? <Loader2 size={13} className="animate-spin" />
                : <Wrench size={13} />}
              Re-point mapping to this file by account name
            </button>
          )}

          {candidates && (
            <div style={S.card} data-testid="mapping-repair-candidates">
              <div style={{ fontSize: 12, fontWeight: 700, color: '#7F1D1D', marginBottom: 8 }}>
                Which saved layout should I rebuild from?
              </div>
              <div style={{ maxHeight: 210, overflowY: 'auto' }}>
                {candidates.candidates.map((c) => (
                  <label key={c.source}
                         data-testid={`repair-source-${c.source}`}
                         style={{ ...S.row,
                                  background: source === c.source ? '#FEE2E2' : 'transparent' }}>
                    <input type="radio" name="repair-source" checked={source === c.source}
                           onChange={() => { setSource(c.source); setPreview(null); }} />
                    <span style={{ color: '#7F1D1D' }}>
                      <strong>{c.label}</strong>
                      {c.source === candidates.recommended_source && ' — best match'}
                      <br />
                      <span style={S.mono}>
                        {c.matched} accounts recoverable · {c.dropped} not found ·{' '}
                        {c.section_consistency}% land in the right section
                      </span>
                    </span>
                  </label>
                ))}
              </div>
              <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                <button style={S.ghost} onClick={runPreview} disabled={!source || busy === 'preview'}
                        data-testid="preview-mapping-repair-btn">
                  {busy === 'preview' ? 'Building preview…' : 'Preview changes'}
                </button>
                {preview && (
                  <button style={S.btn} onClick={apply} disabled={busy === 'apply'}
                          data-testid="apply-mapping-repair-btn">
                    {busy === 'apply' ? 'Applying…' : 'Apply repair'}
                  </button>
                )}
              </div>
            </div>
          )}

          {preview && (
            <div style={S.card} data-testid="mapping-repair-preview">
              <div style={{ fontSize: 12.5, color: '#7F1D1D', lineHeight: 1.8 }}>
                <strong>{preview.repointed_count}</strong> lines re-pointed to the correct new
                account · <strong>{preview.already_correct_count}</strong> already correct ·{' '}
                <strong>{preview.dropped_count}</strong> names not found in this file (dropped) ·{' '}
                <strong>{preview.new_unmapped_count}</strong> new accounts left for review
              </div>
              {preview.dropped_count > 0 && (
                <div style={{ ...S.mono, marginTop: 8, color: '#7F1D1D' }}>
                  Dropped: {preview.dropped.slice(0, 12).map((d) => d.name).join(', ')}
                  {preview.dropped_count > 12 ? ` +${preview.dropped_count - 12} more` : ''}
                </div>
              )}
              {preview.new_unmapped_count > 0 && (
                <div style={{ ...S.mono, marginTop: 6, color: '#7F1D1D' }}>
                  Needs review: {preview.new_unmapped.slice(0, 12).map((a) => a.name).join(', ')}
                  {preview.new_unmapped_count > 12 ? ` +${preview.new_unmapped_count - 12} more` : ''}
                </div>
              )}
              <div style={{ fontSize: 11, color: '#991B1B', marginTop: 8 }}>
                Your current mapping is archived to Mapping History before anything changes.
              </div>
            </div>
          )}

          {error && (
            <div style={{ ...S.mono, marginTop: 10, color: '#7F1D1D' }}
                 data-testid="mapping-repair-error">{error}</div>
          )}
        </div>
      </div>
    </div>
  );
}
